<template>
    <v-container>
        <div class="control-panel">
            <div>
                <div class="controls">
                    <div class="source-switch-wrap">
                        <v-select
                            v-model="sourceModel"
                            class="source-select"
                            :items="sourceMenuItems"
                            return-object
                            color="primary"
                            variant="outlined"
                            persistent-placeholder
                            data-id="SourcesSwitch"
                            hide-details
                        >
                            <template #item="{ item, props: itemProps }: { item: any, props: any }">
                                <v-list-item v-bind="itemProps" :data-id="`Source${item.value}`" />
                            </template>
                        </v-select>
                    </div>
                    <div class="search-wrap">
                        <v-text-field
                            v-model="searchQuery"
                            class="search"
                            append-inner-icon="mdi-magnify"
                            color="primary"
                            data-id="SearchTextField"
                            hide-details
                            variant="outlined"
                            :label="t('messages.resources.searchInTable')"
                            :clearable="true"
                        />
                    </div>
                </div>
                <list-filters
                    ref="listFilterRef"
                    v-model:company="companiesFilterModel"
                    v-model:category="categoriesFilterModel"
                    v-model:line="linesFilterModel"
                    v-model:status="statusFilterModel"
                    :companies="companiesWithAll"
                    :categories="categoryFilterItems"
                    :lines="linesWithAll"
                />
            </div>
            <div v-if="canCreateMessage" class="new-message-wrap">
                <v-btn
                    v-if="hasOnlyOneCategory"
                    class="create-message"
                    color="primary"
                    data-id="AddNewButton"
                    @click="createMessage(categoryTemplates[0].name)"
                >
                    {{ t(`messages.resources.new${categoryTemplates[0].name}`) }}
                </v-btn>
                <v-btn v-else class="create-message" color="primary" data-id="AddNewButton">
                    <span>{{ createMessageMenu ? t(`messages.resources.selectDeviationType`) : t(`messages.resources.${createButtonText}`) }}</span>
                    <v-icon size="large">{{ createMessageMenu ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    <v-menu v-model="createMessageMenu" activator="parent">
                        <v-list>
                            <v-list-item v-for="category in categoryTemplates" :key="category.id" :value="category.name">
                                <v-list-item-title :data-id="`Category${category.name}`" @click="createMessage(category.name)">
                                    {{ category.label }}
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-btn>
            </div>
        </div>
        <v-row no-gutters>
            <v-col class="table-wrap" :class="{ showSummary: showSummary }">
                <v-data-table
                    v-model:expanded="expandedGroupIds"
                    :headers="tableHeaders"
                    :items="tableRows"
                    :items-per-page="DefaultPageSize"
                    :loading="loadingTable"
                    :page="page"
                    :show-expand="true"
                    item-value="groupId"
                    data-id="DataTable"
                    disable-sort
                >
                    <template #item="{ item, internalItem, toggleExpand, isExpanded }: { item: any, internalItem: any, toggleExpand: any, isExpanded: any }">
                        <template v-if="item.isSingle">
                            <message-row
                                :message="item.messages[0]"
                                :headers="tableHeaders"
                                :selected="selectedSingleId === item.messages[0].id"
                                @select="onChildClick"
                            />
                        </template>
                        <template v-else>
                            <tr
                                :class="[groupRowClass(item), { expanded: isExpanded(internalItem) }]"
                                :data-group-id="item.groupId"
                                @click="onGroupRowClick(item, internalItem, toggleExpand, isExpanded)"
                            >
                                <td v-for="header in tableHeaders" :key="header.key">
                                    <template v-if="header.key === 'data-table-expand'">
                                        <v-icon @click.stop="toggleExpand(internalItem)">
                                            {{ isExpanded(internalItem) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                                        </v-icon>
                                    </template>
                                    <template v-else-if="header.key === 'status'">
                                        <v-tooltip bottom>
                                            <template #activator="{ props: itemProps }">
                                                <v-icon
                                                    :color="groupStatusColor(item)"
                                                    :icon="groupStatusIcon(item)"
                                                    v-bind="itemProps"
                                                    size="small"
                                                />
                                            </template>
                                            <span>{{ groupStatusTooltip(item) }}</span>
                                        </v-tooltip>
                                    </template>
                                    <template v-else>{{ groupCell(item, header.key) }}</template>
                                </td>
                            </tr>
                        </template>
                    </template>
                    <template #expanded-row="{ item }: { item: any }">
                        <message-row
                            v-for="child in item.messages"
                            :key="child.id"
                            class="child-row"
                            :message="child"
                            :headers="tableHeaders"
                            :selected="selectedSingleId === child.id"
                            @select="onChildClick"
                        />
                    </template>
                    <template #bottom>
                        <div class="pagination-wrap">
                            <div v-if="messagesStore.paging.totalItemCount !== undefined" class="totalMessages">
                                {{ t('messages.resources.messages') + ': ' + messagesStore.paging.totalItemCount }}
                            </div>
                            <div class="paging-wrap">
                                <v-pagination v-model="page" :length="messagesStore.paging.pageCount" />
                            </div>
                        </div>
                    </template>
                </v-data-table>
                <div v-if="showSummary">
                    <message-group-summary
                        v-if="selectedMode === 'group' && messagesStore.selectedGroup"
                        :group="messagesStore.selectedGroup"
                        :can-unpublish="canUnpublishGroup"
                        :loading="loadingGroup"
                        @close-summary="closeSummary"
                        @unpublish-group="unpublishGroup"
                    />
                    <message-summary
                        v-else
                        v-model="messagesStore.message"
                        :loading="loadingMessage"
                        :loading-update="loadingMessageUpdate"
                        :can-update="canUpdateMessage"
                        :can-publish="canPublishMessage"
                        :can-unpublish="canUnpublishMessage"
                        :message-template="messagesStore.currentMessageTemplate"
                        @update-message="updateMessage"
                        @close-summary="closeSummary"
                        @unpublish="unpublishMessage"
                        @publish="publishMessage"
                    />
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts" setup>
import _ from 'lodash';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useMessagesStore } from '../messagesStore';
import { useLinesStore } from '@/Features/Lines/linesStore';
import { useCompaniesStore } from '@/Features/Companies/companiesStore';
import { DefaultPageSize, AutocompleteItem } from '@/shared/models';
import { DateToUTC, ToLocalDate } from '@/shared/DateFormatHelpers';
import {
    MessageCategoryTemplate,
    MessageListGroupItem,
    MessageListItem,
    MessageSource,
    MessageSourceLink,
    MessageStatus
} from '../messagesModels';
import MessageSummary from './MessageSummary.vue';
import MessageRow from './MessageRow.vue';
import MessageGroupSummary from './MessageGroupSummary.vue';
import { useRouter } from 'vue-router';
import { routeNames } from '@/router';
import { useRouteQuery, integerTransformer } from 'vue-use-route-query';
import { Line } from '@/Features/Lines/linesModels';
import { Company } from '@/Features/Companies/companiesModels';
import { useListHeaders } from './useListHeaders';
import ListFilters from './ListFilters.vue';
import { hasLink } from '@/shared/LinkHelper';
import { MessagesRels } from '../rels';

interface GroupTableRow {
    groupId: string;
    isSingle: boolean;
    messages: MessageListItem[];
    data: MessageListGroupItem;
}

const router = useRouter();
const { t } = useI18n();
const messagesStore = useMessagesStore();
const linesStore = useLinesStore();
const companiesStore = useCompaniesStore();

const page = useRouteQuery('page', 1, integerTransformer, { mode: 'push' });
const selectedSingleId = useRouteQuery('messageId', '');
const selectedGroupQueryId = useRouteQuery('groupSel', '');
const expandedQuery = useRouteQuery('expanded', '');
const searchQuery = useRouteQuery('search', '');
const sourceQuery = useRouteQuery('source', '');
const filterStatus = useRouteQuery('status', '');
const filterLine = useRouteQuery('line', '');
const filterCompany = useRouteQuery('company', '');
const filterCategory = useRouteQuery('category', '');

const createMessageMenu = ref(false);

const { query, categoryTemplates, source, sourceLinks, filters, categoryFilterItems } = storeToRefs(messagesStore);
const { linesWithAll } = storeToRefs(linesStore);
const { companiesWithAll } = storeToRefs(companiesStore);
const currentDate = computed(() => DateToUTC(new Date()));
const loadingTable = ref(true);
const showSummary = ref(false);
const selectedMode = ref<'single' | 'group' | null>(null);
const hasOnlyOneCategory = computed(() => categoryTemplates.value.length === 1);
const canCreateMessage = computed(() => hasLink(MessagesRels.createMessage, messagesStore.links));
const canUpdateMessage = computed(() => hasLink(MessagesRels.updateMessage, messagesStore.messageLinks));
const canPublishMessage = computed(() => hasLink(MessagesRels.publishMessage, messagesStore.messageLinks));
const canUnpublishMessage = computed(() => hasLink(MessagesRels.unpublishMessage, messagesStore.messageLinks));
const canUnpublishGroup = computed(() => hasLink(MessagesRels.unpublishMessageGroup, messagesStore.selectedGroup?.links ?? []));
const createButtonText = computed(() => (hasOnlyOneCategory.value ? `new${categoryTemplates.value[0].name}` : 'newMessage'));
const loadingMessage = ref(false);
const loadingMessageUpdate = ref(false);
const loadingGroup = ref(false);

const { ticketingMachineHeaders, portalHeaders } = useListHeaders();
const expandHeader = { key: 'data-table-expand', title: '', sortable: false, width: 48 };
const tableHeaders = computed<any[]>(() => [
    expandHeader,
    ...(source.value === MessageSource.TicketingMachine ? ticketingMachineHeaders.value : portalHeaders.value)
]);
const sourceModel = ref<AutocompleteItem<MessageSourceLink>>();
const sourceMenuItems = ref<AutocompleteItem<MessageSourceLink>[]>();

const listFilterRef = ref<InstanceType<typeof ListFilters> | null>(null);
const statusFilterModel = ref<AutocompleteItem<string>>();
const linesFilterModel = ref<AutocompleteItem<Line>>();
const companiesFilterModel = ref<AutocompleteItem<Company>>();
const categoriesFilterModel = ref<AutocompleteItem<MessageCategoryTemplate | null>>();

const expandedGroupIds = ref<string[]>([]);

const tableRows = computed<GroupTableRow[]>(() =>
    messagesStore.groups.map(g => ({
        groupId: g.groupId,
        isSingle: g.messages.length === 1,
        messages: g.messages,
        data: g
    }))
);

onMounted(async () => {
    query.value = searchQuery.value;
    if (sourceQuery.value.length) source.value = sourceQuery.value as MessageSource;
    if (filterStatus.value.length) filters.value.status = filterStatus.value;
    if (filterLine.value.length) filters.value.lineName = filterLine.value;
    if (filterCompany.value.length) filters.value.companyNumber = filterCompany.value;
    if (filterCategory.value.length) filters.value.categoryName = filterCategory.value;
    if (messagesStore.categoryTemplates.length < 1) await messagesStore.loadMessageCategories();
    if (messagesStore.messageTemplates.length < 1) await messagesStore.loadMessageTemplates();
    if (!linesWithAll.value.length) await linesStore.loadLines();
    if (!companiesWithAll.value.length) await companiesStore.loadCompanies();
    await messagesStore.loadMessageGroups(page.value);
    await messagesStore.loadMessageCategoriesBySource();
    expandedGroupIds.value = expandedQuery.value ? expandedQuery.value.split(',').filter(Boolean) : [];
    await openSelectedFromUrl();

    sourceMenuItems.value = sourceLinks.value.map(x => toSelectItem(x));
    sourceModel.value = sourceMenuItems.value.find(x => x.value === source.value);
    listFilterRef.value?.initFilters(filters.value);

    loadingTable.value = false;
});

watch(expandedGroupIds, (val, oldVal) => {
    if (val.length > 1) {
        // Accordion behaviour: keep only the most recently expanded group.
        const newest = val.find(id => !oldVal?.includes(id)) ?? val[val.length - 1];
        expandedGroupIds.value = [newest];
        return;
    }
    expandedQuery.value = val.join(',');
});

watch(linesFilterModel, async (newVal, oldVal) => {
    if (!newVal || oldVal === undefined) return;
    await updateFilter(newVal.data.name, x => { filters.value.lineName = x; filterLine.value = x; });
});
watch(categoriesFilterModel, async (newVal, oldVal) => {
    if (!newVal || oldVal === undefined) return;
    await updateFilter(newVal.value, x => { filters.value.categoryName = x; filterCategory.value = x; });
});
watch(companiesFilterModel, async (newVal, oldVal) => {
    if (!newVal || oldVal === undefined) return;
    await updateFilter(newVal.data.companyNumber, x => { filters.value.companyNumber = x; filterCompany.value = x; });
});
watch(statusFilterModel, async (newVal, oldVal) => {
    if (!newVal || oldVal === undefined) return;
    await updateFilter(newVal.value, x => { filters.value.status = x; filterStatus.value = x; });
});

async function updateFilter(value: string, callback: (val: string) => void) {
    loadingTable.value = true;
    const newValue = value === 'All' || value === '0' ? '' : value;
    callback(newValue);
    await messagesStore.loadMessageGroups(1);
    loadingTable.value = false;
}

watch(sourceModel, async (newVal, oldVal) => {
    if (!newVal || oldVal === undefined) return;
    sourceQuery.value = newVal.value;
    source.value = newVal.data.source;
    closeSummary();
    loadingTable.value = true;
    if (oldVal) {
        await messagesStore.loadMessageCategoriesBySource();
        listFilterRef.value?.resetCategoryFilter();
    }
    await messagesStore.loadMessageGroups(1);
    loadingTable.value = false;
});

watch(
    searchQuery,
    _.debounce(async val => {
        loadingTable.value = true;
        page.value = 1;
        query.value = val;
        closeSummary();
        await messagesStore.loadMessageGroups(page.value);
        loadingTable.value = false;
    }, 500)
);

watch(page, async () => {
    loadingTable.value = true;
    closeSummary();
    await messagesStore.loadMessageGroups(page.value);
    loadingTable.value = false;
});

async function onChildClick(message: MessageListItem) {
    loadingMessage.value = true;
    selectedMode.value = 'single';
    showSummary.value = true;
    selectedSingleId.value = message.id;
    selectedGroupQueryId.value = '';
    // Collapse the expanded group when selecting a row that doesn't belong to it.
    if (!expandedGroupIds.value.includes(message.groupId)) {
        expandedGroupIds.value = [];
    }
    await messagesStore.loadMessage(message.id);
    loadingMessage.value = false;
}

function onGroupRowClick(row: GroupTableRow, internalItem: any, toggleExpand: (item: any) => void, isExpanded: (item: any) => boolean) {
    loadingGroup.value = true;
    selectedMode.value = 'group';
    showSummary.value = true;
    selectedGroupQueryId.value = row.groupId;
    selectedSingleId.value = '';
    messagesStore.loadGroup(row.groupId);
    if (!isExpanded(internalItem)) toggleExpand(internalItem);
    loadingGroup.value = false;
}

async function openSelectedFromUrl() {
    if (selectedSingleId.value) {
        const child = messagesStore.messages.find(m => m.id === selectedSingleId.value);
        if (child) await onChildClick(child);
    } else if (selectedGroupQueryId.value) {
        const group = messagesStore.groups.find(g => g.groupId === selectedGroupQueryId.value);
        if (group) {
            selectedMode.value = 'group';
            showSummary.value = true;
            messagesStore.loadGroup(group.groupId);
            if (!expandedGroupIds.value.includes(group.groupId)) {
                expandedGroupIds.value = [...expandedGroupIds.value, group.groupId];
            }
        }
    }
}

async function updateMessage() {
    loadingMessageUpdate.value = true;
    await messagesStore.updateMessage();
    loadingMessageUpdate.value = false;
}

function closeSummary() {
    showSummary.value = false;
    selectedMode.value = null;
    selectedSingleId.value = '';
    selectedGroupQueryId.value = '';
}

async function unpublishMessage() {
    setSingleLoading(true);
    await messagesStore.unpublishMessage();
    await messagesStore.loadMessageGroups(page.value);
    setSingleLoading(false);
    closeSummary();
}

async function publishMessage() {
    setSingleLoading(true);
    await messagesStore.publishMessage();
    await messagesStore.loadMessageGroups(page.value);
    setSingleLoading(false);
    closeSummary();
}

async function unpublishGroup() {
    loadingGroup.value = true;
    loadingTable.value = true;
    await messagesStore.unpublishGroup();
    await messagesStore.loadMessageGroups(page.value);
    if (selectedGroupQueryId.value) messagesStore.loadGroup(selectedGroupQueryId.value);
    loadingGroup.value = false;
    loadingTable.value = false;
}

function setSingleLoading(isLoading: boolean) {
    loadingTable.value = isLoading;
    loadingMessage.value = isLoading;
}

function createMessage(category: string) {
    router.push({ name: routeNames.messageCreate, query: { category } });
}

function toSelectItem(link: MessageSourceLink): AutocompleteItem<MessageSourceLink> {
    return { title: t(`messages.resources.${link.translationKey}`), value: link.source, data: link };
}

function groupRowClass(row: GroupTableRow): string {
    const c: string[] = [];
    if (selectedGroupQueryId.value === row.groupId) c.push('selected-group');
    const g = row.data;
    if (g.hasMixedStatus) {
        c.push('mixed-group');
    } else if (g.status !== MessageStatus.Published) {
        c.push('unpublished-message');
    } else if (groupIsActive(g)) {
        c.push('active-message');
    } else if (groupIsExpired(g)) {
        c.push('expired-message');
    } else {
        c.push('future-message');
    }
    return c.join(' ');
}

function groupIsActive(g: MessageListGroupItem): boolean {
    if (!g.startPublish || !g.endPublish) return false;
    const start = DateToUTC(g.startPublish);
    const end = DateToUTC(g.endPublish);
    return start < currentDate.value && currentDate.value < end && g.status === MessageStatus.Published;
}

function groupIsExpired(g: MessageListGroupItem): boolean {
    return currentDate.value > DateToUTC(g.endPublish);
}

function groupStatusColor(row: GroupTableRow): string {
    if (row.data.hasMixedStatus) return 'amber-darken-2';
    if (row.data.status === MessageStatus.Published) return 'primary';
    if (row.data.status === MessageStatus.Unpublished) return 'red-darken-4';
    return 'grey';
}

function groupStatusIcon(row: GroupTableRow): string {
    if (row.data.hasMixedStatus) return 'mdi-circle-half-full';
    if (row.data.status === MessageStatus.Published) return 'mdi-check-circle';
    if (row.data.status === MessageStatus.Unpublished) return 'mdi-close-circle';
    return 'mdi-circle';
}

function groupStatusTooltip(row: GroupTableRow): string {
    if (row.data.hasMixedStatus) return t('messages.resources.mixedStatusTooltip');
    if (row.data.status === MessageStatus.Published) return t('messages.resources.published');
    if (row.data.status === MessageStatus.Unpublished) return t('messages.resources.unpublished');
    return t('messages.resources.draft');
}

function groupCell(row: GroupTableRow, key: string): string {
    const g = row.data;
    switch (key) {
        case 'category': return g.categoryNameNo;
        case 'reason': return g.reasonNo;
        case 'validFrom': return `${ToLocalDate(g.validFrom, 'date')} - ${ToLocalDate(g.validFrom, 'time')}`;
        case 'validTo': return `${ToLocalDate(g.validTo, 'date')} - ${ToLocalDate(g.validTo, 'time')}`;
        case 'author': return g.author ?? '';
        case 'scope': return g.sentTo ?? '';
        case 'lineName':
        case 'departureTime':
        case 'departure':
        case 'driverId':
            return '';
        default:
            return '';
    }
}
</script>

<style lang="scss" scoped>
@use '@/shared/variables.scss' as *;

.control-panel {
    display: grid;
    grid-template-columns: 50% 50%;
    padding-top: 42px;
    padding-bottom: 24px;

    .controls {
        display: grid;
        grid-template-columns: 50% 50%;
        padding-bottom: 22px;

        .search,
        .source-select {
            margin-right: 16px;
        }
    }

    .filter-title {
        padding-bottom: 6px;
        font-size: 14px;
        font-weight: 500;
    }

    .filters {
        display: grid;
        grid-template-columns: 25% 25% 25% 25%;

        .v-autocomplete {
            margin-right: 16px;
        }
    }
}

@media screen and (max-width: 1038px) {
    .control-panel {
        grid-template-columns: 75% 25%;
    }
}

.new-message-wrap {
    .activeBtn {
        color: $white !important;
        background-color: $primary;
    }

    .create-message {
        float: right;
    }
}

.v-table :deep(.v-data-table-header__content) {
    font-size: 18px;
    font-weight: bold;
    color: $black;
}

.pagination-wrap {
    padding: 12px;

    .totalMessages {
        color: $grey;
        display: inline-block;
    }

    .paging-wrap {
        display: block;
        width: 33%;
        float: right;

        :deep(.v-pagination__list) {
            justify-content: right;
        }
    }
}

.table-wrap {
    display: grid;
    grid-template-columns: 100% 0;

    .item-summary {
        display: none;
    }

    &.showSummary {
        grid-template-columns: calc(100% - 372px) 372px;

        .item-summary {
            display: block;
        }
    }

    :deep(table) {
        .selected-message {
            td {
                border-top: 1px solid $primary;
                border-bottom: 1px solid $primary !important;

                &:first-child {
                    border-left: 1px solid $primary;
                }

                &:last-child {
                    border-right: 1px solid $primary;
                }
            }

            &.unpublished-message {
                td {
                    border-color: $red-darken-4 !important;
                }
            }
        }

        .future-message {
            &:hover,
            &.selected-message,
            &.selected-group,
            &.expanded {
                background-color: $darkenedWhite;
            }
        }

        .unpublished-message {
            background-color: $disabledRed;

            &:hover,
            &.selected-message,
            &.selected-group,
            &.expanded {
                background-color: $darkenedRed;
            }
        }

        .active-message {
            background-color: $lightGreen;

            &:hover,
            &.selected-message,
            &.selected-group,
            &.expanded {
                background-color: $darkenedGreen;
            }
        }

        .expired-message {
            background-color: $lightGrey;

            &:hover,
            &.selected-message,
            &.selected-group,
            &.expanded {
                background-color: $darkenedGrey;
            }
        }

        .mixed-group {
            background-color: $lightAmber;

            &:hover,
            &.selected-message,
            &.selected-group,
            &.expanded {
                background-color: $darkenedAmber;
            }
        }

        .child-row {
            background-color: $borderGrey;

            &:hover,
            &.selected-message {
                background-color: $darkenedGrey;
            }
        }

        .selected-group {
            td {
                border-top: 1px solid $primary;
                border-bottom: 1px solid $primary !important;

                &:first-child { border-left: 1px solid $primary; }
                &:last-child { border-right: 1px solid $primary; }
            }
        }
    }
}
</style>
